import React, { useState } from 'react';
import { ArrowLeftCircle, ArrowRightCircle, Edit, Trash2, XCircle, Calendar, Info, User, PlusCircle, Save, XOctagon } from 'react-feather';
import { api, Variable, DataPoint, DataPointValue } from '../api/client';
import { formatDateWithoutCurrentYear, formatShortTime } from '../utils/date';
import { useSnackbar } from '../context/SnackbarContext';

interface PaginationProps {
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

interface Props {
  questionId: string;
  variables: Variable[];
  dataPoints: DataPoint[];
  pagination: PaginationProps;
  onPageChange: (page: number) => void | Promise<void>;
  onDataChanged: () => void;
}

const formatNow = (): string => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const getDisplayUserName = (dataPoint: DataPoint): string => {
  return dataPoint.created_by.name || dataPoint.created_by.id;
};

const getNumericValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const hasInputValue = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  return value !== undefined && value !== null && value !== '';
};

const toDataPointValues = (
  variables: Variable[],
  inputValues: Record<string, any>,
  inputDescriptions: Record<string, string>
): Record<string, DataPointValue> => {
  const convertedValues: Record<string, DataPointValue> = {};
  for (const variable of variables) {
    const rawValue = inputValues[variable.id];
    let value: number | string | boolean | null;
    if (!hasInputValue(rawValue)) {
      value = null;
    } else if (variable.type === 'boolean') {
      value = rawValue === 'true' || rawValue === true;
    } else if (variable.type === 'numeric') {
      value = parseFloat(rawValue);
    } else {
      value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    }
    const dpValue: DataPointValue = { value };
    if (value !== null && inputDescriptions[variable.id]) {
      dpValue.description = inputDescriptions[variable.id];
    }
    convertedValues[variable.id] = dpValue;
  }
  return convertedValues;
};

const DataPointManager: React.FC<Props> = ({
  questionId,
  variables,
  dataPoints,
  pagination,
  onPageChange,
  onDataChanged,
}) => {
  const { showSnackbar } = useSnackbar();
  const [showAddModal, setShowAddModal] = useState(false);
  const [values, setValues] = useState<Record<string, any>>({});
  const [descriptions, setDescriptions] = useState<Record<string, string>>({});
  const [recordedAt, setRecordedAt] = useState<string>(formatNow());
  const [submitting, setSubmitting] = useState(false);
  const [deletingDataPointId, setDeletingDataPointId] = useState<string | null>(null);
  const [editingDataPoint, setEditingDataPoint] = useState<DataPoint | null>(null);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [editDescriptions, setEditDescriptions] = useState<Record<string, string>>({});
  const [editRecordedAt, setEditRecordedAt] = useState<string>('');
  const [categoricalValues, setCategoricalValues] = useState<Record<string, string[]>>({});
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!variables.some(v => hasInputValue(values[v.id]))) {
      showSnackbar('Please provide at least one value.', { variant: 'error' });
      return;
    }

    try {
      setSubmitting(true);

      const convertedValues = toDataPointValues(variables, values, descriptions);

      // Convert local datetime to ISO 8601 format
      const recordedAtISO = new Date(recordedAt).toISOString();

      await api.createDataPoint(questionId, convertedValues, recordedAtISO);
      setShowAddModal(false);
      setValues({});
      setDescriptions({});
      setRecordedAt(formatNow());
      showSnackbar('Data point added.', { variant: 'success' });
      onDataChanged();
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to save data point', { variant: 'error' });
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenAdd = async () => {
    setValues({});
    setDescriptions({});
    setRecordedAt(formatNow());
    setShowAddModal(true);
    const catVars = variables.filter(v => v.type === 'categorical');
    if (catVars.length > 0) {
      const entries = await Promise.all(
        catVars.map(async v => [v.id, await api.listCategoricalValues(v.id)] as [string, string[]])
      );
      setCategoricalValues(Object.fromEntries(entries));
    }
  };

  const handleCancelAdd = () => {
    setShowAddModal(false);
    setValues({});
    setDescriptions({});
  };

  const handleEditClick = async (dp: DataPoint) => {
    // Initialize edit values and descriptions from the datapoint
    const initialValues: Record<string, any> = {};
    const initialDescriptions: Record<string, string> = {};
    for (const variable of variables) {
      const dpValue = dp.values[variable.id];
      if (dpValue !== undefined) {
        if (dpValue.value === null) {
          initialValues[variable.id] = '';
        } else if (variable.type === 'boolean') {
          initialValues[variable.id] = dpValue.value ? 'true' : 'false';
        } else {
          initialValues[variable.id] = dpValue.value;
        }
        if (dpValue.description) {
          initialDescriptions[variable.id] = dpValue.description;
        }
      }
    }
    setEditValues(initialValues);
    setEditDescriptions(initialDescriptions);

    // Initialize recorded_at from the datapoint's created_at
    const dpDate = new Date(dp.created_at);
    const formatted = new Date(dpDate.getTime() - dpDate.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setEditRecordedAt(formatted);

    setEditingDataPoint(dp);

    const catVars = variables.filter(v => v.type === 'categorical');
    if (catVars.length > 0) {
      const entries = await Promise.all(
        catVars.map(async v => [v.id, await api.listCategoricalValues(v.id)] as [string, string[]])
      );
      setCategoricalValues(Object.fromEntries(entries));
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDataPoint) return;

    if (!variables.some(v => hasInputValue(editValues[v.id]))) {
      showSnackbar('Please provide at least one value.', { variant: 'error' });
      return;
    }

    try {
      setSubmitting(true);

      const convertedValues = toDataPointValues(variables, editValues, editDescriptions);

      // Convert local datetime to ISO 8601 format
      const recordedAtISO = new Date(editRecordedAt).toISOString();

      await api.updateDataPoint(editingDataPoint.id, {
        values: convertedValues,
        recorded_at: recordedAtISO,
      });
      setEditingDataPoint(null);
      setEditValues({});
      setEditDescriptions({});
      setEditRecordedAt('');
      showSnackbar('Data point updated.', { variant: 'success' });
      onDataChanged();
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to update data point', { variant: 'error' });
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingDataPoint(null);
    setEditValues({});
    setEditDescriptions({});
    setEditRecordedAt('');
  };

  const handleDeleteClick = async (dp: DataPoint) => {
    if (!window.confirm('Delete this data point? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingDataPointId(dp.id);
      await api.deleteDataPoint(dp.id);
      showSnackbar('Data point deleted.', { variant: 'success' });
      onDataChanged();
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to delete data point', { variant: 'error' });
      console.error(err);
    } finally {
      setDeletingDataPointId(null);
    }
  };

  const formatValue = (value: any, variable: Variable): string => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    if (variable.type === 'boolean') {
      return value ? 'Yes' : 'No';
    } else if (variable.type === 'numeric') {
      return typeof value === 'number' ? value.toFixed(2) : value.toString();
    }
    return value.toString();
  };

  const numericMaximums = variables.reduce<Record<string, number>>((acc, variable) => {
    if (variable.type !== 'numeric') {
      return acc;
    }

    const maxValue = dataPoints.reduce((max, dataPoint) => {
      const numericValue = getNumericValue(dataPoint.values?.[variable.id]?.value);
      if (numericValue === null) {
        return max;
      }
      return Math.max(max, Math.abs(numericValue));
    }, 0);

    acc[variable.id] = maxValue;
    return acc;
  }, {});

  const getBarWidth = (variableId: string, value: unknown): string => {
    const numericValue = getNumericValue(value);
    const maximum = numericMaximums[variableId];
    if (numericValue === null || !maximum) {
      return '0%';
    }

    const percentage = Math.max(10, Math.min(100, (Math.abs(numericValue) / maximum) * 100));
    return `${percentage}%`;
  };

  const getValueToneStyle = (variable: Variable): React.CSSProperties => {
    if (variable.type === 'boolean') {
      return {
        ['--dp-accent' as string]: 'var(--wnl-ok)',
        ['--dp-accent-soft' as string]: 'var(--wnl-ok-tint)',
      };
    }

    if (variable.type === 'categorical') {
      return {
        ['--dp-accent' as string]: 'var(--wnl-primary)',
        ['--dp-accent-soft' as string]: 'var(--wnl-primary-tint)',
      };
    }

    if (variable.dependent) {
      return {
        ['--dp-accent' as string]: 'var(--wnl-data)',
        ['--dp-accent-soft' as string]: 'var(--wnl-data-tint)',
      };
    }

    return {
      ['--dp-accent' as string]: 'var(--wnl-text-muted)',
      ['--dp-accent-soft' as string]: 'var(--wnl-subtle)',
    };
  };

  return (
    <div className="data-point-manager">
      <div className="section-header">
        <h3>{pagination.total_count} datapoints</h3>
        <button className="btn btn-primary btn-sm" onClick={() => void handleOpenAdd()}>
          <PlusCircle size={14} />
          Add datapoint
        </button>
      </div>

      {/* Previously Logged Data Points */}
      {dataPoints.length > 0 ? (
        <>
          <div className="data-points-list">
            {dataPoints.map((dp) => (
              <article key={dp.id} className="data-point-card dp-row">
                <div className="data-point-meta">
                  <div className="date-cell">
                    <span className="date-line">
                      {formatDateWithoutCurrentYear(dp.created_at)}{' '}
                      <span className="date-time">{formatShortTime(dp.created_at)}</span>
                    </span>
                  </div>
                  <div className="recorded-by">
                    <User size={14} className="recorded-by-icon" aria-hidden="true" />
                    <span className="recorded-by-name">{getDisplayUserName(dp)}</span>
                  </div>
                </div>
                <div className="data-point-cells">
                  {variables.map((v) => {
                    const dpv = dp.values?.[v.id];
                    const desc = dpv?.description || undefined;
                    const value = dpv !== undefined ? formatValue(dpv.value, v) : '-';
                    const isEmpty = dpv === undefined || dpv.value === null;
                    const cellClassName = [
                      'data-point-cell',
                      `data-point-cell-${v.type}`,
                      isEmpty ? 'is-empty' : null,
                    ]
                      .filter(Boolean)
                      .join(' ');

                    return (
                      <div
                        key={v.id}
                        className={cellClassName}
                        style={getValueToneStyle(v)}
                      >
                        <div className="data-point-cell-label">
                          <span className="data-point-cell-dot" aria-hidden="true" />
                          <span className="data-point-cell-title">{v.name}</span>
                        </div>
                        {v.type === 'numeric' ? (
                          <div className="data-point-cell-value data-point-cell-value-numeric">
                            <div className="data-point-bar" aria-hidden="true">
                              <span
                                className="data-point-bar-fill"
                                style={{ width: getBarWidth(v.id, dpv?.value) }}
                              />
                            </div>
                            <div className="data-point-value-meta">
                              <span className="data-point-value-text">{value}</span>
                              <span className="value-description-slot">
                                {desc ? (
                                  <span className="value-description-tooltip" data-tooltip={desc} tabIndex={0}>
                                    <Info size={11} className="value-info-icon" />
                                  </span>
                                ) : (
                                  <span className="value-description-placeholder" aria-hidden="true" />
                                )}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="data-point-cell-value">
                            {isEmpty ? (
                              <span className="data-point-value-empty">-</span>
                            ) : (
                              <span className="data-point-value-pill">{value}</span>
                            )}
                            {desc && (
                              <span className="value-description-tooltip" data-tooltip={desc} tabIndex={0}>
                                <Info size={11} className="value-info-icon" />
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="row-actions">
                  <button
                    className="btn-icon"
                    title="Edit"
                    onClick={() => void handleEditClick(dp)}
                    disabled={deletingDataPointId === dp.id}
                  >
                    <Edit size={14} />
                  </button>
                  <button
                    className="btn-icon btn-icon-danger"
                    title="Delete"
                    onClick={() => void handleDeleteClick(dp)}
                    disabled={deletingDataPointId === dp.id}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </article>
            ))}
          </div>
          {pagination.total_pages > 1 && (
            <div className="pagination-controls">
              <button
                className="btn btn-secondary btn-sm"
                disabled={pagination.page <= 1}
                onClick={() => onPageChange(pagination.page - 1)}
              >
                <ArrowLeftCircle size={14} />
                Previous
              </button>
              <span className="pagination-label">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <button
                className="btn btn-secondary btn-sm"
                disabled={pagination.page >= pagination.total_pages}
                onClick={() => onPageChange(pagination.page + 1)}
              >
                Next
                <ArrowRightCircle size={14} />
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="empty-state">No data points logged yet.</p>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={handleCancelAdd}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add datapoint</h2>
              <button className="btn-close" onClick={handleCancelAdd}><XCircle size={18} /></button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="recorded-at">
                  When was this recorded?
                  <small>The date and time when this observation was made</small>
                </label>
                <div className="date-input-wrapper">
                  <input
                    id="recorded-at"
                    type="datetime-local"
                    value={recordedAt}
                    onChange={(e) => setRecordedAt(e.target.value)}
                    required
                  />
                  <button type="button" className="date-input-icon" onClick={() => (document.getElementById('recorded-at') as HTMLInputElement)?.showPicker()}>
                    <Calendar size={15} />
                  </button>
                </div>
              </div>

              {variables.map((variable) => (
                <div key={variable.id} className="form-group">
                  <label htmlFor={variable.id}>
                    {variable.name}
                    {variable.description && <small>{variable.description}</small>}
                  </label>

                  {variable.type === 'boolean' ? (
                    <select
                      id={variable.id}
                      value={values[variable.id] ?? ''}
                      onChange={(e) => setValues({ ...values, [variable.id]: e.target.value })}
                    >
                      <option value="">Select...</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : variable.type === 'numeric' ? (
                    <input
                      id={variable.id}
                      type="number"
                      step="any"
                      value={values[variable.id] ?? ''}
                      onChange={(e) => setValues({ ...values, [variable.id]: e.target.value })}
                      placeholder="Enter a number"
                    />
                  ) : (
                    <input
                      id={variable.id}
                      type="text"
                      value={values[variable.id] ?? ''}
                      onChange={(e) => setValues({ ...values, [variable.id]: e.target.value })}
                      list={categoricalValues[variable.id]?.length > 0 ? `${variable.id}-categorical-values` : undefined}
                      placeholder={categoricalValues[variable.id]?.length > 0 ? 'Select or type a new value' : 'Enter a value'}
                    />
                  )}
                  {variable.type === 'categorical' && categoricalValues[variable.id]?.length > 0 && (
                    <datalist id={`${variable.id}-categorical-values`}>
                      {categoricalValues[variable.id].map((val) => (
                        <option key={val} value={val} />
                      ))}
                    </datalist>
                  )}
                  <input
                    id={`${variable.id}-description`}
                    type="text"
                    value={descriptions[variable.id] ?? ''}
                    onChange={(e) => setDescriptions({ ...descriptions, [variable.id]: e.target.value })}
                    placeholder="Optional description (e.g., notes about this value)"
                    className="description-input"
                  />
                </div>
              ))}

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCancelAdd}
                  className="btn btn-secondary"
                  disabled={submitting}
                >
                  <XOctagon size={14} />
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  <Save size={14} />
                  {submitting ? 'Saving...' : 'Add datapoint'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingDataPoint && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Data Point</h2>
              <button className="btn-close" onClick={handleCancelEdit}><XCircle size={18} /></button>
            </div>

            <form onSubmit={handleEditSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="edit-recorded-at">
                  When was this recorded?
                  <small>The date and time when this observation was made</small>
                </label>
                <div className="date-input-wrapper">
                  <input
                    id="edit-recorded-at"
                    type="datetime-local"
                    value={editRecordedAt}
                    onChange={(e) => setEditRecordedAt(e.target.value)}
                    required
                  />
                  <button type="button" className="date-input-icon" onClick={() => (document.getElementById('edit-recorded-at') as HTMLInputElement)?.showPicker()}>
                    <Calendar size={15} />
                  </button>
                </div>
              </div>

              {variables.map((variable) => (
                <div key={variable.id} className="form-group">
                  <label htmlFor={`edit-${variable.id}`}>
                    {variable.name}
                    {variable.description && <small>{variable.description}</small>}
                  </label>

                  {variable.type === 'boolean' ? (
                    <select
                      id={`edit-${variable.id}`}
                      value={editValues[variable.id] ?? ''}
                      onChange={(e) => setEditValues({ ...editValues, [variable.id]: e.target.value })}
                    >
                      <option value="">Select...</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : variable.type === 'numeric' ? (
                    <input
                      id={`edit-${variable.id}`}
                      type="number"
                      step="any"
                      value={editValues[variable.id] ?? ''}
                      onChange={(e) => setEditValues({ ...editValues, [variable.id]: e.target.value })}
                      placeholder="Enter a number"
                    />
                  ) : (
                    <input
                      id={`edit-${variable.id}`}
                      type="text"
                      value={editValues[variable.id] ?? ''}
                      onChange={(e) => setEditValues({ ...editValues, [variable.id]: e.target.value })}
                      list={categoricalValues[variable.id]?.length > 0 ? `edit-${variable.id}-categorical-values` : undefined}
                      placeholder={categoricalValues[variable.id]?.length > 0 ? 'Select or type a new value' : 'Enter a value'}
                    />
                  )}
                  {variable.type === 'categorical' && categoricalValues[variable.id]?.length > 0 && (
                    <datalist id={`edit-${variable.id}-categorical-values`}>
                      {categoricalValues[variable.id].map((val) => (
                        <option key={val} value={val} />
                      ))}
                    </datalist>
                  )}
                  <input
                    id={`edit-${variable.id}-description`}
                    type="text"
                    value={editDescriptions[variable.id] ?? ''}
                    onChange={(e) => setEditDescriptions({ ...editDescriptions, [variable.id]: e.target.value })}
                    placeholder="Optional description (e.g., notes about this value)"
                    className="description-input"
                  />
                </div>
              ))}

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="btn btn-secondary"
                  disabled={submitting}
                >
                  <XOctagon size={14} />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  <Save size={14} />
                  {submitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataPointManager;
